/**
 * Source UNIQUE des hôtes hub et suffixes tenant multi-tenant.
 *
 * Partagée entre l'API (Node) et l'ERP (navigateur) pour qu'un ajout d'hôte ne
 * puisse JAMAIS diverger entre les deux côtés — une divergence « hub vs tenant »
 * entre front et back = bug d'isolation. Cf docs/multitenancy.md.
 *
 * IMPORTANT : ce module doit rester importable côté navigateur ET Node. Il ne
 * contient QUE des constantes et des fonctions PURES : aucun accès à `process`
 * ni `import.meta`. L'accès à l'environnement DIFFÈRE entre les deux côtés
 * (`process.env` côté API, `import.meta.env` côté ERP) : chaque consommateur lit
 * SA variable et la passe en paramètre aux helpers ci-dessous.
 *
 * La LOGIQUE de résolution (hub vs tenant, extraction/validation du slug) reste
 * volontairement dans chaque consommateur — on ne mutualise ici QUE la liste des
 * hôtes/suffixes de référence.
 */

/** Hôtes hub officiels prod/staging (correspondance EXACTE, pas suffixe). */
export const HOTES_HUB_OFFICIELS: readonly string[] = ["app.crayea.fr", "app.champ-erp.fr"];

/** Suffixes de sous-domaine tenant officiels prod/staging. */
export const SUFFIXES_TENANT_OFFICIELS: readonly string[] = [".crayea.fr", ".champ-erp.fr"];

/** Hôtes hub reconnus en dev / test local (correspondance EXACTE). */
export const HOTES_HUB_DEV: readonly string[] = ["localhost", "app.localhost", "127.0.0.1"];

/** Suffixe de sous-domaine tenant en dev local. */
export const SUFFIXE_TENANT_DEV = ".localhost";

/**
 * Normalise le domaine racine additionnel : trim + lowercase, `null` si vide.
 * Un `""` ou une chaîne d'espaces devient `null` (pas de domaine additionnel).
 */
function normaliserRacineAdditionnelle(extraRoot?: string | null): string | null {
  const racine = extraRoot?.trim().toLowerCase();
  return racine ? racine : null;
}

/**
 * Construit la liste des hôtes hub prod/staging, en ajoutant `app.<extraRoot>`
 * si un domaine racine additionnel est fourni (déploiement de test / preview
 * servi ailleurs que sur `crayea.fr`).
 *
 * PUR : ne lit pas l'environnement. L'appelant passe sa propre variable
 * (`PUBLIC_ROOT_DOMAIN` côté API, `VITE_PUBLIC_ROOT_DOMAIN` côté ERP) — la
 * normalisation (trim/lowercase/vide→ignoré) est faite ici.
 */
export function construireHotesHub(extraRoot?: string | null): string[] {
  const racine = normaliserRacineAdditionnelle(extraRoot);
  return [...HOTES_HUB_OFFICIELS, ...(racine ? [`app.${racine}`] : [])];
}

/**
 * Construit la liste des suffixes tenant prod/staging, en ajoutant `.<extraRoot>`
 * si un domaine racine additionnel est fourni. PUR (cf `construireHotesHub`).
 */
export function construireSuffixesTenant(extraRoot?: string | null): string[] {
  const racine = normaliserRacineAdditionnelle(extraRoot);
  return [...SUFFIXES_TENANT_OFFICIELS, ...(racine ? [`.${racine}`] : [])];
}
